import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
    MagnifyingGlass, Buildings, Phone, MapPin, ArrowRight, Stethoscope,
    Handshake, Calendar, ChartBar, PaperPlaneTilt, CheckCircle, X,
} from "@phosphor-icons/react";

/* ===========================================================================
   B2BLeadsList — Path B Phase 3 (2026-02-25)
   ---------------------------------------------------------------------------
   Sales pipeline list for B2B provider acquisition leads. Used at:
     - /coe/sales/b2b-leads (sales rep sees only their own — server-scoped)

   Status pipeline:
     new → contacted → discovery_done → qualified → application_sent
         → applied → handed_to_admin → won / lost

   Pipeline filter chips at the top show counts per status. Each row collapses
   the lead onto a single card: clinic + contact + role + city + cases/mo +
   status pill + age. Click → /coe/sales/b2b-leads/:id.
   =========================================================================== */

const STATUS_PILLS = {
    new:               { label: "New",                 cls: "bg-sky-100 text-sky-700 border-sky-200" },
    contacted:         { label: "Contacted",           cls: "bg-violet-100 text-violet-700 border-violet-200" },
    discovery_done:    { label: "Discovery done",      cls: "bg-indigo-100 text-indigo-700 border-indigo-200" },
    qualified:         { label: "Qualified",           cls: "bg-amber-100 text-amber-800 border-amber-200" },
    // iter98.99ey — Demo confirmed status. Slots between qualified and
    // application_sent because the demo typically precedes the invite.
    demo_scheduled:    { label: "Demo scheduled",      cls: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200" },
    application_sent:  { label: "Invite sent",         cls: "bg-cyan-100 text-cyan-800 border-cyan-200" },
    applied:           { label: "Applied",             cls: "bg-teal-100 text-teal-800 border-teal-200" },
    handed_to_admin:   { label: "Handed to admin",     cls: "bg-blue-100 text-blue-800 border-blue-200" },
    // iter98.99fb — Pipeline rewired: PES Accept = "approved" (doctor live);
    // "won" now means kickoff happened + fully onboarded.
    approved:          { label: "Accepted",            cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    application_incomplete: { label: "Docs pending",   cls: "bg-amber-100 text-amber-800 border-amber-200" },
    // iter98.99fa — Auto-marked after 3 follow-ups with no doctor reply.
    unresponsive:      { label: "Unresponsive",        cls: "bg-rose-100 text-rose-700 border-rose-200" },
    won:               { label: "Won — partner live",  cls: "bg-emerald-100 text-emerald-700 border-emerald-200" },
    lost:              { label: "Lost",                cls: "bg-secondary text-muted-foreground border-border" },
};

const ROLE_LABEL = {
    orthodontist: "Orthodontist",
    gp_dentist: "GP dentist",
    clinic_admin: "Clinic admin",
    other: "Other",
};

const FILTERS = [
    // iter98.99fg — Added the 3 statuses that were silently excluded:
    // demo_scheduled (kickoff booked but not done), approved (PES accepted
    // but kickoff still pending), application_incomplete (PES chasing
    // missing docs). All three represent OPEN PES work and belong in
    // the default "Open" view.
    { id: "open",  label: "Open",  statuses: ["new", "contacted", "discovery_done", "qualified", "demo_scheduled", "application_sent", "applied", "approved", "application_incomplete", "handed_to_admin"] },
    { id: "new",   label: "New" },
    { id: "qualified", label: "Qualified" },
    { id: "application_sent", label: "Invite sent" },
    { id: "applied",   label: "Applied" },
    { id: "approved",  label: "Accepted" },
    { id: "unresponsive", label: "Unresponsive" },
    { id: "won",       label: "Won" },
    { id: "lost",      label: "Lost" },
    { id: "all",       label: "All" },
];


export default function B2BLeadsList({ scope = "sales" }) {
    const [leads, setLeads] = useState([]);
    const [stats, setStats] = useState({});
    const [filter, setFilter] = useState("open");
    const [q, setQ] = useState("");
    const [loading, setLoading] = useState(true);

    // The two endpoints differ only by URL prefix (/sales vs /admin).
    // `scope` lets us reuse this list shell for both surfaces.
    const basePath = scope === "admin" ? "/admin/b2b-leads" : "/sales/b2b-leads";
    const statsPath = "/sales/b2b-leads/stats";   // sales endpoint works for admin too (admin scope is "all")

    useEffect(() => {
        setLoading(true);
        Promise.all([
            api.get(basePath),
            api.get(statsPath).catch(() => ({ data: {} })),
        ])
            .then(([list, s]) => {
                setLeads(list.data || []);
                setStats(s.data || {});
            })
            .finally(() => setLoading(false));
    }, [basePath]);

    const filtered = useMemo(() => {
        let out = leads;
        const searching = q.trim().length > 0;
        // iter98.99fh — When the user is actively searching, BYPASS the
        // status filter so terminal/won leads (e.g. fully onboarded doctors
        // currently sitting on "won" while the active chip is "Open") are
        // still reachable by typing their name. Status chips only apply
        // when the search box is empty.
        if (!searching) {
            const f = FILTERS.find((x) => x.id === filter);
            if (f && f.statuses) {
                out = out.filter((l) => f.statuses.includes(l.status));
            } else if (filter !== "all") {
                out = out.filter((l) => l.status === filter);
            }
        }
        if (searching) {
            const low = q.toLowerCase();
            out = out.filter((l) =>
                (l.clinic || "").toLowerCase().includes(low) ||
                (l.contact_name || "").toLowerCase().includes(low) ||
                (l.city || "").toLowerCase().includes(low) ||
                (l.email || "").toLowerCase().includes(low) ||
                (l.phone || "").includes(q)
            );
        }
        return out;
    }, [leads, filter, q]);

    const headerStats = [
        { label: "Open pipeline", val: stats.open_total ?? 0, icon: Buildings, accent: "text-primary" },
        { label: "Qualified",     val: stats.qualified ?? 0,  icon: ChartBar,  accent: "text-amber-600" },
        { label: "Invites sent",  val: stats.application_sent ?? 0, icon: PaperPlaneTilt, accent: "text-cyan-600" },
        { label: "Won",           val: stats.won ?? 0,        icon: Handshake, accent: "text-emerald-600" },
    ];

    return (
        <div data-testid="b2b-leads-list">
            <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
                <div>
                    <h1 className="font-display text-3xl font-bold" data-testid="b2b-leads-page-title">
                        {scope === "admin" ? "B2B leads (all reps)" : "B2B leads"}
                    </h1>
                    <p className="text-muted-foreground mt-1 text-sm">
                        {scope === "admin"
                            ? "Cross-rep visibility into the partner-acquisition pipeline. Reassign to balance load."
                            : "Partner-clinic acquisition pipeline. Move leads from cold inbound to active partners."}
                    </p>
                </div>
                <div className="relative w-full max-w-xs">
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        placeholder="Search clinic, contact, city, phone — finds across all statuses"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="pl-9 h-10"
                        data-testid="b2b-leads-search"
                    />
                </div>
            </div>

            {/* Header stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
                {headerStats.map((s) => (
                    <div key={s.label} className="bg-card border border-border rounded-2xl p-4" data-testid={`b2b-stat-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                        <div className="flex items-center justify-between mb-2">
                            <s.icon size={18} weight="duotone" className={s.accent} />
                        </div>
                        <div className="text-2xl font-display font-bold tabular-nums">{s.val}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{s.label}</div>
                    </div>
                ))}
            </div>

            {/* Status filter chips */}
            <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="b2b-leads-filter">
                {FILTERS.map((f) => {
                    const count = f.statuses
                        ? f.statuses.reduce((acc, s) => acc + (stats[s] || 0), 0)
                        : (f.id === "all"
                            ? (leads.length || Object.values(stats).filter((v, i) => Number.isFinite(v) && i < 9).reduce((a, b) => a + b, 0))
                            : (stats[f.id] || 0));
                    const active = filter === f.id;
                    return (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${active ? "bg-primary text-white border-primary" : "border-border hover:bg-secondary"}`}
                            data-testid={`b2b-filter-${f.id}`}
                        >
                            {f.label}
                            <span className={`ml-1.5 tabular-nums ${active ? "text-white/70" : "text-muted-foreground"}`}>{count}</span>
                        </button>
                    );
                })}
            </div>

            {loading && <div className="text-muted-foreground">Loading…</div>}

            {!loading && filtered.length === 0 && (
                <EmptyState filter={filter} hasLeads={leads.length > 0} searching={q.trim().length > 0} query={q.trim()} />
            )}

            <div className="bg-card border border-border rounded-2xl overflow-hidden">
                {filtered.map((lead) => (
                    <LeadRow key={lead.id} lead={lead} scope={scope} />
                ))}
            </div>
        </div>
    );
}


function LeadRow({ lead, scope }) {
    const status = STATUS_PILLS[lead.status] || STATUS_PILLS.new;
    const linkBase = scope === "admin" ? "/admin/b2b-leads" : "/coe/sales/b2b-leads";
    const updatedAt = lead.updated_at ? new Date(lead.updated_at) : null;
    return (
        <Link
            to={`${linkBase}/${lead.id}`}
            className="block px-5 py-4 border-b border-border last:border-0 hover:bg-secondary/40 transition-colors"
            data-testid={`b2b-row-${lead.id}`}
        >
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                        <Buildings size={15} weight="duotone" className="text-primary shrink-0" />
                        <span className="font-display font-bold truncate">{lead.clinic}</span>
                        <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded border ${status.cls} shrink-0`}>
                            {status.label}
                        </span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground flex-wrap">
                        <span className="flex items-center gap-1"><Stethoscope size={12} /> {lead.contact_name}</span>
                        <span className="opacity-50">·</span>
                        <span>{ROLE_LABEL[lead.role] || lead.role}</span>
                        {lead.city && <><span className="opacity-50">·</span><span className="flex items-center gap-1"><MapPin size={12} /> {lead.city}</span></>}
                        {lead.cases_per_month && <><span className="opacity-50">·</span><span>{lead.cases_per_month} cases/mo</span></>}
                        <span className="opacity-50">·</span>
                        <span className="flex items-center gap-1"><Phone size={12} /> {lead.phone}</span>
                    </div>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                    <div className="text-right">
                        {scope === "admin" && lead.assigned_sales_name && (
                            <div className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">
                                {lead.assigned_sales_name}
                            </div>
                        )}
                        <div className="text-[10px] text-muted-foreground">
                            {updatedAt ? `Updated ${updatedAt.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}` : ""}
                        </div>
                    </div>
                    <ArrowRight size={16} className="text-muted-foreground" />
                </div>
            </div>
        </Link>
    );
}


function EmptyState({ filter, hasLeads, searching, query }) {
    const f = FILTERS.find((x) => x.id === filter);
    let message;
    if (!hasLeads) {
        message = "No B2B leads yet. New demo-request submissions on /for-orthodontists will appear here.";
    } else if (searching) {
        message = `No leads match "${query}". Search looks across every status — try a different name, clinic, city, phone, or email.`;
    } else {
        message = `No leads in "${f?.label || filter}".`;
    }
    return (
        <div className="bg-card border border-border rounded-2xl p-10 text-center text-muted-foreground" data-testid="b2b-leads-empty">
            {message}
        </div>
    );
}

/* Export the icon set so the admin clone can reuse if needed */
export { STATUS_PILLS, ROLE_LABEL };
